import { computed, reactive, readonly } from 'vue'
import { debounce, orderBy, sortBy } from 'lodash-es'

import type { PaginatedFolderList } from '@openapi'
import {
  type Folder,
  type FileRetrieve,
  type FolderRetrieve,
  type MiniFile,
  type MiniPage,
  type FolderFolder,
  type File,
  type FavoriteCreateRequest,
  type Favorite,
  type FolderCreateRequest,
  type FolderUpdateRequest,
  type FileUpdateRequest,
  type BulkDeleteRequest,
  type BulkDeleteResponse,
  type FolderFile,
  FileStatusEnum,
} from '@openapi'
import useAppStore from '@browser/stores/app'
import type { GetFavoritesParams } from '@lib/types/favorites'
import type { PaginatedData } from '@lib/types/paginatedData'
import type { AllFileCreateRequest, DeviceFile } from '@lib/types/files'
import type { RecentFile } from '@lib/types/recentFiles.types'
import type { Mutable } from '@lib/types/ts'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import type { FolderListRequest } from '@sdk/payload.types'
type CompactFolder = Mutable<FolderFolder>
type FolderFileWithFolder = FolderFile & {
  folder_id?: string
  folder: { id: string; name: string }
  type?: 'file' | 'folder'
}

const PENDING_FILES_POLLING_INTERVAL = 8000

type FolderItemsFetcher = (
  folderId: Folder['id'] | undefined,
  options?: { justFetch: boolean; setSelectedFolder: boolean; forceRefresh?: boolean },
) => Promise<FolderRetrieve>
type FolderCreator = (payload: Omit<FolderCreateRequest, 'instance_id'>) => Promise<FolderRetrieve>
type FolderUpdater = (payload: FolderUpdateRequest & { id: Folder['id'] }) => Promise<FolderRetrieve>
type FolderDeleter = (payload: Folder['id']) => Promise<void>
type FolderFetcher = (payload: Folder['id']) => Promise<FolderRetrieve>
type FoldersDeleter = (payload: BulkDeleteRequest) => Promise<BulkDeleteResponse>
type FolderLister = (payload: FolderListRequest) => Promise<PaginatedFolderList>
type FileCreator = (payload: Omit<AllFileCreateRequest, 'instance_id'>) => Promise<FileRetrieve>
type FileUpdater = (payload: FileUpdateRequest & { id: File['id'] }) => Promise<FileRetrieve>
type FileOpener = (payload: { fileId: MiniFile['id']; pageIndex?: MiniPage['page_index']; newTab?: boolean }) => void
type FileDeleter = (payload: MiniFile['id']) => Promise<MiniFile['id']>
type FilesDeleter = (payload: BulkDeleteRequest) => Promise<BulkDeleteResponse>
type FileFetcher = (payload: File['id']) => Promise<FileRetrieve>
type FileListFetcher = (params?: any) => Promise<File[]>
type FileListSearchFetcher = (params?: any) => Promise<void>
type FilesFoldersDeleter = (payload: BulkDeleteRequest) => Promise<BulkDeleteResponse>
type FavoriteCreator = (payload: FavoriteCreateRequest) => Promise<Favorite>
type FavoriteDeleter = (payload: Favorite['id']) => Promise<void>
type FavoritesDeleter = (payload: BulkDeleteRequest) => Promise<BulkDeleteResponse>
type FavoritesFetcher = (payload?: GetFavoritesParams) => Promise<PaginatedData<Favorite>>
type FavoritesSetter = (favorites: Favorite[]) => void
type RecentFilesFetcher = () => Promise<RecentFile[]>
type SelectDeviceFile = () => Promise<DeviceFile>
type CompactFile = Pick<
  File,
  | 'id'
  | 'name'
  | 'thumbnail_url'
  | 'tags'
  | 'created_by'
  | 'modified_at'
  | 'status'
  | 'content_type'
  | 'permissions'
  | 'owned_by'
>
type ItemsMoverItem = { id: File['id'] | Folder['id']; type: 'file' | 'folder' }
type ItemsMover = (folderId: Folder['id'], items: ItemsMoverItem[]) => Promise<FolderRetrieve>

const getInitialState = () => ({
  fileOpener: null,
  fileDeleter: null,
  filesDeleter: null,
  folderItemsFetcher: null,
  folderCreator: null,
  folderUpdater: null,
  folderDeleter: null,
  folderFetcher: null,
  foldersDeleter: null,
  fileCreator: null,
  fileUpdater: null,
  fileFetcher: null,
  fileListFetcher: null,
  filesFoldersDeleter: null,
  favoritesFetcher: null,
  favoriteCreator: null,
  favoriteDeleter: null,
  favoritesDeleter: null,
  favoritesSetter: null,
  recentFilesFetcher: null,
  itemsMover: null,
  folderLister: null,
  convertingFileIdsTimeout: null,
  isFetchingFolderItems: false,
  fetchingFolderId: '',
  isUpdatingFolderItems: false,
  isFetchingListFiles: false,
  isFetchingSelectedFile: false,
  isFetchingSelectedFolder: false,
  isFetchingFavorites: false,
  isCreatingFavorite: false,
  isDeletingFavorite: false,
  isFetchingRecentFiles: false,
  // fetched data
  folders: [], // folders in current folder
  files: [], // files in current folder
  recent: [], // recent files
  selectedFile: null, // selected file
  selectedFolder: null, // selected folder
  favorites: {}, // favorites
  currentFolderPath: [], // breadcrumbs
  currentFolderName: '', // current folder name
  currentFolderId: '', // current folder id
  sectionFieldsDict: {}, // instance metadata template fields dictionary
  searchQuery: '',
  searchMetadataFilters: {},
  filteredItems: [],
  folderCache: {},
})

const state = reactive<{
  fileOpener: FileOpener | null
  fileDeleter: FileDeleter | null
  filesDeleter: FilesDeleter | null
  folderItemsFetcher: FolderItemsFetcher | null
  folderCreator: FolderCreator | null
  folderUpdater: FolderUpdater | null
  folderDeleter: FolderDeleter | null
  folderFetcher: FolderFetcher | null
  foldersDeleter: FoldersDeleter | null
  fileCreator: FileCreator | null
  fileUpdater: FileUpdater | null
  fileFetcher: FileFetcher | null
  fileListFetcher: FileListSearchFetcher | null
  filesFoldersDeleter: FilesFoldersDeleter | null
  favoritesFetcher: FavoritesFetcher | null
  favoriteCreator: FavoriteCreator | null
  favoriteDeleter: FavoriteDeleter | null
  favoritesDeleter: FavoritesDeleter | null
  favoritesSetter: FavoritesSetter | null
  recentFilesFetcher: RecentFilesFetcher | null
  selectDeviceFile?: SelectDeviceFile | null
  itemsMover: ItemsMover | null
  folderLister: FolderLister | null
  convertingFileIdsTimeout: ReturnType<typeof setTimeout> | null
  isUpdatingFolderItems: boolean
  isFetchingFolderItems: boolean
  fetchingFolderId: Folder['id']
  isFetchingListFiles: boolean
  isFetchingSelectedFile: boolean
  isFetchingSelectedFolder: boolean
  isFetchingRecentFiles: boolean
  isFetchingFavorites: boolean
  isCreatingFavorite: boolean
  isDeletingFavorite: boolean
  folders: CompactFolder[]
  files: CompactFile[]
  recent: RecentFile[]
  selectedFile: FileRetrieve | null
  selectedFolder: FolderRetrieve | null
  favorites: Record<MiniPage['id'] | File['id'] | Folder['id'], Favorite>
  currentFolderPath: Awaited<ReturnType<FolderItemsFetcher>>['path']
  currentFolderName: Awaited<ReturnType<FolderItemsFetcher>>['name']
  currentFolderId: Awaited<ReturnType<FolderItemsFetcher>>['id']
  sectionFieldsDict: Record<string, any>
  searchQuery: string
  searchMetadataFilters: Record<string, any>
  filteredItems: FolderFileWithFolder[]
  folderCache: Record<
    string,
    {
      files_count: number
      folders_count: number
      available_files_count?: number
      available_folders_count?: number
      timestamp: number
    }
  >
}>(getInitialState())

const fileOrderedStatuses = [FileStatusEnum.PENDING, FileStatusEnum.VIEWABLE]

const getters = {
  sortedFiles: computed<CompactFile[]>((): CompactFile[] => {
    return orderBy(
      state.files,
      [
        (file: CompactFile) => fileOrderedStatuses.indexOf(file.status),
        (file: CompactFile) => new Date(file.modified_at),
      ],
      ['asc', 'desc'],
    ) // 'asc' for status, 'desc' for modified_at
  }),
  hasMetadataFilters: computed<boolean>(() => {
    return Object.keys(state.searchMetadataFilters).length > 0
  }),
  isLoading: computed<boolean>(() => {
    if (useAppStore().selectedFileId.value) {
      return state.isFetchingSelectedFile
    } else if (useAppStore().selectedFolderId.value) {
      return state.isFetchingSelectedFolder
    } else {
      return false
    }
  }),
}

function convertFavoritesToDict(favorites: Favorite[]) {
  return favorites.reduce(
    (dict, favorite) => {
      const id = favorite.page?.id ?? favorite.file?.id ?? favorite.folder?.id
      if (id) {
        dict[id] = favorite
      }
      return dict
    },
    {} as Record<MiniPage['id'] | File['id'] | Folder['id'], Favorite>,
  )
}

function setFavorites(favorites: Favorite[]) {
  state.favorites = convertFavoritesToDict(favorites)
}

function setCurrentFolder(
  folderId: FolderRetrieve['id'] = getInitialState().currentFolderId,
  folderName: FolderRetrieve['name'] = getInitialState().currentFolderName,
  folderPath: FolderRetrieve['path'] = getInitialState().currentFolderPath,
) {
  state.currentFolderId = folderId
  state.currentFolderName = folderName
  state.currentFolderPath = folderPath
}

function setSearchQuery(searchQuery: string) {
  state.searchQuery = searchQuery
}

function setIsFetchingListFiles(value: boolean) {
  state.isFetchingListFiles = value
}

function setSearchMetadataFilters(filters: Record<string, any>) {
  state.searchMetadataFilters = filters
}

function setFilteredItems(items: FolderFileWithFolder[]) {
  state.filteredItems = items
}

async function onUpsertFolder(name: string, initialName?: string) {
  const updatingFolderId = useAppStore().updatingFolderId
  const setCurrentState = useAppStore().setCurrentState

  try {
    if (initialName && updatingFolderId.value) {
      await state.folderUpdater?.({
        name,
        id: updatingFolderId.value,
      })
      setCurrentState('browse')
    } else if (!initialName && state.currentFolderId) {
      await state.folderCreator?.({
        name,
        parent_folder_id: state?.currentFolderId,
      })
      setCurrentState('browse')
    }
  } catch (error) {
    console.error('error onUpsertFolder: ', error)
  }
}

function updateFolderState(folder: FolderRetrieve) {
  state.folders = folder.folders
  state.files = folder.files
  state.currentFolderPath = folder.path

  // Cache the counts for each subfolder when we load a folder
  folder.folders.forEach((subfolder) => {
    state.folderCache[subfolder.id] = {
      files_count: subfolder.files_count,
      folders_count: subfolder.folders_count,
      available_files_count: subfolder.available_files_count,
      available_folders_count: subfolder.available_folders_count,
      timestamp: Date.now(),
    }
  })

  if (useAppStore().showFilteredResults.value) {
    const filesById = new Map(state.files.map((file) => [file.id, file]))

    state.filteredItems = state.filteredItems.map((filteredItem) => {
      const fileToUpdate = filesById.get(filteredItem.id)
      if (fileToUpdate)
        return {
          ...fileToUpdate,
          folder: filteredItem.folder,
          folder_id: filteredItem.folder_id,
          type: filteredItem?.type,
        } as FolderFileWithFolder

      return filteredItem
    })
  }

  state.currentFolderName = folder.name
  state.currentFolderId = folder.id
}

function clearTimeouts() {
  state.convertingFileIdsTimeout && clearTimeout(state.convertingFileIdsTimeout)
}

function init({
  onFolderFetch,
  onFolderCreate,
  onFolderUpdate,
  onFolderDelete,
  onFoldersDelete,
  onFileCreate,
  onFileUpdate,
  onFileOpen,
  onFileDelete,
  onFilesDelete,
  onFileFetch,
  onFilesList,
  onItemsMove,
  onFavoriteCreate,
  onFavoriteDelete,
  onFavoritesDelete,
  onFavoritesFetch,
  onRecentFilesFetch,
  onSelectDeviceFile,
  onFolderList,
}: {
  onFolderFetch: FolderItemsFetcher
  onFolderCreate: FolderCreator
  onFolderUpdate: FolderUpdater
  onFolderDelete: FolderDeleter
  onFoldersDelete: FoldersDeleter
  onFileCreate: FileCreator
  onFileUpdate: FileUpdater
  onFileOpen: FileOpener
  onFileDelete: FileDeleter
  onFilesDelete: FilesDeleter
  onFileFetch: FileFetcher
  onFilesList: FileListFetcher
  onItemsMove: ItemsMover
  onFavoriteCreate: FavoriteCreator
  onFavoriteDelete: FavoriteDeleter
  onFavoritesDelete: FavoritesDeleter
  onFavoritesFetch: FavoritesFetcher
  onRecentFilesFetch: RecentFilesFetcher
  onSelectDeviceFile?: SelectDeviceFile
  onFolderList: FolderLister
}) {
  let id = 0
  const isCallOutdated = (localId: number) => id > localId

  function pollForConvertingFileIds(data: FolderRetrieve) {
    const convertingFileIds = (data?.files ?? [])
      .filter((file) => file?.status === FileStatusEnum.PENDING)
      .map((file) => file?.id)

    if (convertingFileIds.length) {
      state.convertingFileIdsTimeout && clearTimeout(state.convertingFileIdsTimeout)
      state.convertingFileIdsTimeout = setTimeout(
        () => getFolderById(data.id).then((data) => state.currentFolderId === data.id && updateFolderState(data)),
        PENDING_FILES_POLLING_INTERVAL,
      )
    } else {
      state.convertingFileIdsTimeout && clearTimeout(state.convertingFileIdsTimeout)
    }

    return data
  }
  function getFolderById(id?: Folder['id']) {
    return onFolderFetch(id).then((r) => pollForConvertingFileIds(r))
  }

  state.folderLister = async function (payload: FolderListRequest) {
    return onFolderList?.(payload)
  }

  state.selectDeviceFile = onSelectDeviceFile ?? null

  state.folderItemsFetcher = async function (
    folderId: Folder['id'] | undefined,
    options: {
      justFetch: boolean
      setSelectedFolder: boolean
      forceRefresh?: boolean
    } = {
      justFetch: false,
      setSelectedFolder: false,
      forceRefresh: false,
    },
  ) {
    const localId = ++id

    if (options.justFetch) {
      return await getFolderById(folderId)
    }

    if (options.setSelectedFolder) {
      try {
        state.isFetchingSelectedFolder = true
        const response = await getFolderById(folderId)

        if (!options.forceRefresh && isCallOutdated(localId)) {
          return response
        }

        state.selectedFolder = response

        return response
      } finally {
        state.isFetchingSelectedFolder = false
      }
    }

    try {
      state.isFetchingFolderItems = true
      state.fetchingFolderId = folderId ?? ''
      if (folderId === state.currentFolderId) {
        state.isUpdatingFolderItems = true
      }

      const response = await getFolderById(folderId)

      if (isCallOutdated(localId)) {
        return response
      }

      useAppStore().resetSelectedItems()
      updateFolderState(response)

      return response
    } finally {
      state.fetchingFolderId = ''
      state.isUpdatingFolderItems = false
      state.isFetchingFolderItems = false
    }
  }

  state.folderCreator = async function (payload: { name: string; parent_folder_id?: Folder['id'] }) {
    const folder = await onFolderCreate({
      ...payload,
      parent_folder_id: payload.parent_folder_id as string,
    })
    state.folders = [
      ...state.folders,
      {
        ...folder,
        thumbnail_url: null,
        file_thumbnail_urls: [],
        folders_count: 0,
        files_count: 0,
        available_folders_count: 0,
        available_files_count: 0,
        canvases_count: 0,
        canvas_thumbnail_urls: [],
      },
    ]

    // Add newly created folder to cache with zero counts
    state.folderCache[folder.id] = {
      files_count: 0,
      folders_count: 0,
      available_files_count: 0,
      available_folders_count: 0,
      timestamp: Date.now(),
    }

    return folder
  }
  state.folderUpdater = async function (payload: { id: Folder['id']; name: string }) {
    const folder = await onFolderUpdate(payload)
    const { id } = payload
    state.folders = state.folders.map((prevFolder) =>
      prevFolder.id === folder.id ? { ...prevFolder, ...folder } : prevFolder,
    )

    if (state.favorites[id]) {
      state.favorites = {
        ...state.favorites,
        [id]: {
          ...state.favorites[id],
          folder: { ...state.favorites[id].folder, name: folder.name },
          name: folder.name,
        } as Favorite,
      }
    }

    return folder
  }
  state.folderDeleter = async function (payload: Folder['id']) {
    await onFolderDelete(payload)
    state.folders = state.folders.filter((folder) => folder.id !== payload)

    // Remove deleted folder from cache
    delete state.folderCache[payload]

    useAppStore().removeSelectedItems([payload])
  }
  state.folderFetcher = async function (folderId: Folder['id']) {
    try {
      state.isFetchingFolderItems = true
      state.fetchingFolderId = folderId ?? ''
      if (folderId === state.currentFolderId) {
        state.isUpdatingFolderItems = true
      }

      return await getFolderById(folderId)
    } finally {
      state.fetchingFolderId = ''
      state.isUpdatingFolderItems = false
      state.isFetchingFolderItems = false
    }
  }
  state.foldersDeleter = async function (payload: BulkDeleteRequest) {
    const response = await onFoldersDelete(payload)
    const deletedIdsSet = new Set(response.deleted_ids)
    state.folders = state.folders.filter((folder) => !deletedIdsSet.has(folder.id))

    // Remove deleted folders from cache
    response.deleted_ids.forEach((id) => {
      delete state.folderCache[id]
    })

    useAppStore().removeSelectedItems(response.deleted_ids)
    return response
  }
  state.fileOpener = onFileOpen
  state.fileDeleter = async function (fileId: MiniFile['id']) {
    const id = await onFileDelete(fileId)
    state.files = state.files.filter((file) => file.id !== id)
    state.filteredItems = state.filteredItems.filter((file) => file.id !== id)
    delete state.favorites[id]
    state.recent = state.recent.filter((file) => file.id !== id)

    useAppStore().removeSelectedItems([fileId])
    return id
  }
  state.filesDeleter = async function (fileIds: BulkDeleteRequest) {
    const response = await onFilesDelete(fileIds)
    const deletedIdsSet = new Set(response.deleted_ids)
    state.files = state.files.filter((file) => !deletedIdsSet.has(file.id))
    state.filteredItems = state.filteredItems.filter((file) => !deletedIdsSet.has(file.id))
    useAppStore().removeSelectedItems(response.deleted_ids)
    return response
  }
  state.fileCreator = async function (payload: Omit<AllFileCreateRequest, 'instance_id'>) {
    const file = await onFileCreate(payload)
    state.files = [...state.files, file]

    if (state.filteredItems) {
      state.filteredItems = sortBy(
        [...state.filteredItems, file as unknown as FolderFileWithFolder].map((f) => ({ ...f, type: 'file' })),
        [(f: FolderFileWithFolder) => f.folder_id !== state.currentFolderId, 'name'],
      )
    }

    return file
  }

  state.fileUpdater = async function (payload: { id: File['id']; name?: File['name']; tags?: File['tags'] }) {
    const file = await onFileUpdate(payload)
    const { id } = payload
    const index = state.files.findIndex((f) => f.id === id)
    if (index !== -1) state.files.splice(index, 1, file)

    state.filteredItems = state.filteredItems.map((f) =>
      f.id === id ? { ...f, name: file.name ?? '', tags: file.tags ?? [] } : f,
    )
    state.recent = state.recent.map((f) => (f.id === id ? { ...f, name: file.name, tags: file.tags } : f))

    if (state.favorites[id]) {
      state.favorites = {
        ...state.favorites,
        [id]: {
          ...state.favorites[id],
          file: { ...state.favorites[id].file, name: file.name, tags: file.tags },
          name: file.name,
        } as Favorite,
      }
    }

    return file
  }
  state.fileFetcher = async function (fileId: File['id']) {
    try {
      state.isFetchingSelectedFile = true
      const response = await onFileFetch(fileId)
      state.selectedFile = response
      return response
    } finally {
      state.isFetchingSelectedFile = false
    }
  }

  state.fileListFetcher = async function () {
    try {
      state.isFetchingListFiles = true

      const { isAdmin } = useAppStore()

      // Prepare file search request
      const fileSearchPromise = onFilesList({
        metadataFilters: normalizeFilterParams(state.searchMetadataFilters),
        search: state.searchQuery,
        ...(isAdmin.value ? { include_expired: true } : {}),
      })

      // Only search for folders if no metadata filters are applied
      const hasMetadataFilters = getters.hasMetadataFilters.value
      const folderSearchPromise =
        !hasMetadataFilters && !useAppStore().isLocalSearch.value
          ? state.folderLister?.({
              search: state.searchQuery,
            }) || Promise.resolve(null)
          : Promise.resolve(null)

      // Run requests (either in parallel or just files)
      const [filesResult, foldersResponse] = await Promise.allSettled([fileSearchPromise, folderSearchPromise])

      // Process files if request succeeded
      let filteredFiles: any[] = []
      if (filesResult.status === 'fulfilled') {
        const r = filesResult.value as unknown as FolderFileWithFolder[]
        filteredFiles = sortBy(
          r
            .filter((f) => {
              // Admin users: include all viewable files (including expired ones)
              if (isAdmin.value) {
                return f.status === 'viewable'
              } else {
                // Non-admin users: check both status and expiration
                const isViewable = f.status === 'viewable'
                const isNotExpired = !f.expires_at || new Date(f.expires_at) > new Date()
                return isViewable && isNotExpired
              }
            })
            .map((f) => ({ ...f, type: 'file' })),
          [(f) => f.folder_id !== state.currentFolderId, 'name'],
        )
      } else {
        console.error('Failed to fetch files:', filesResult.reason)
      }

      // Process folders if request succeeded and metadata filters weren't applied
      let filteredFolders: any[] = []
      if (!hasMetadataFilters && foldersResponse.status === 'fulfilled' && foldersResponse.value) {
        const foldersFromSearch = (foldersResponse.value.results ?? []) as any[]

        // Find folders that aren't cached and fetch their parents to cache them
        const foldersToCache = foldersFromSearch.filter(
          (folder) => !state.folderCache[folder.id] && folder.parent_folder_id,
        )

        if (foldersToCache.length > 0) {
          // Get unique parent IDs that need fetching
          const parentIdsToFetch = Array.from(new Set(foldersToCache.map((f) => f.parent_folder_id).filter(Boolean)))

          // Fetch all parents to cache their subfolders
          await Promise.all(
            parentIdsToFetch.map(async (parentId) => {
              try {
                const parentFolder = await state.folderItemsFetcher?.(parentId, {
                  justFetch: true,
                  setSelectedFolder: false,
                })

                if (parentFolder) {
                  // Cache the parent folder itself
                  const isAdmin = useAppStore().appName.value === 'admin'

                  state.folderCache[parentId] = {
                    files_count: parentFolder.files.length,
                    folders_count: parentFolder.folders.length,
                    available_files_count: parentFolder.files.filter((f: any) => {
                      const isExpired = f.expires_at && new Date(f.expires_at) < new Date()
                      return !f.deleted_at && (isAdmin || !isExpired) && f.status === 'viewable'
                    }).length,
                    available_folders_count: parentFolder.folders.filter((sf: any) => {
                      return (
                        (sf.files_count > 0 || sf.folders_count > 0) &&
                        (sf.available_files_count !== 0 || sf.available_folders_count !== 0)
                      )
                    }).length,
                    timestamp: Date.now(),
                  }

                  // Cache all subfolders
                  parentFolder.folders.forEach((subfolder) => {
                    state.folderCache[subfolder.id] = {
                      files_count: subfolder.files_count,
                      folders_count: subfolder.folders_count,
                      available_files_count: subfolder.available_files_count,
                      available_folders_count: subfolder.available_folders_count,
                      timestamp: Date.now(),
                    }
                  })
                }
              } catch (error) {
                console.error(`Failed to fetch parent folder ${parentId}:`, error)
              }
            }),
          )
        }

        // Filter folders based on cached data
        filteredFolders = foldersFromSearch
          .map((folder) => {
            const cached = state.folderCache[folder.id]
            const parentCached = folder.parent_folder_id ? state.folderCache[folder.parent_folder_id] : null
            const isAdmin = useAppStore().appName.value === 'admin'

            // Only filter empty folders for non-admin apps
            if (!isAdmin) {
              if (
                cached &&
                cached.available_files_count !== undefined &&
                cached.available_folders_count !== undefined &&
                cached.available_files_count === 0 &&
                cached.available_folders_count === 0
              ) {
                return null // Filter out
              }

              // If parent is cached and has available_folders_count: 0
              if (
                parentCached &&
                parentCached.available_folders_count !== undefined &&
                parentCached.available_folders_count === 0
              ) {
                return null // Filter out
              }
            }

            if (cached) {
              const result: any = {
                ...folder,
                type: 'folder',
              }

              // Only add count properties if they exist
              if (cached.files_count !== undefined) result.files_count = cached.files_count
              if (cached.folders_count !== undefined) result.folders_count = cached.folders_count
              if (cached.available_files_count !== undefined)
                result.available_files_count = cached.available_files_count
              if (cached.available_folders_count !== undefined) {
                result.available_folders_count = cached.available_folders_count
              }

              return result
            }

            return { ...folder, type: 'folder' }
          })
          .filter((folder) => folder !== null) // Remove filtered folders
      } else if (!hasMetadataFilters && !useAppStore().isLocalSearch.value) {
        console.error(
          'Failed to fetch folders:',
          foldersResponse.status === 'rejected' ? foldersResponse.reason : 'No folder lister available',
        )
      }

      state.filteredItems = [...filteredFolders, ...filteredFiles]
    } finally {
      state.isFetchingListFiles = false
    }
  }

  state.filesFoldersDeleter = async function (payload: BulkDeleteRequest) {
    const fileIdsSet = new Set(useAppStore().displayedFiles.value.map((file) => file.id))
    const folderIdsSet = new Set(state.folders.map((folder) => folder.id))

    const fileIds: File['id'][] = []
    const folderIds: Folder['id'][] = []

    payload.ids.forEach((id) => {
      if (folderIdsSet.has(id)) {
        folderIds.push(id)
      } else if (fileIdsSet.has(id)) {
        fileIds.push(id)
      } else {
        console.warn('Could not find item to delete:', id)
      }
    })

    const fileDeletionResult = fileIds.length > 0 ? await state.filesDeleter?.({ ids: fileIds }) : null
    const folderDeletionResult = folderIds.length > 0 ? await state.foldersDeleter?.({ ids: folderIds }) : null

    const allDeletedIds = [...(fileDeletionResult?.deleted_ids || []), ...(folderDeletionResult?.deleted_ids || [])]
    const allErrors = [...(fileDeletionResult?.errors || []), ...(folderDeletionResult?.errors || [])]

    return { deleted_ids: allDeletedIds, errors: allErrors }
  }
  state.itemsMover = async function (...args) {
    try {
      state.isFetchingFolderItems = true
      const response = await onItemsMove(...args)
      useAppStore().resetSelectedItems()

      state.folders = response.folders
      state.files = response.files
      state.currentFolderPath = response.path
      state.currentFolderName = response.name
      state.currentFolderId = response.id

      await state.fileListFetcher?.() // to update the folder name, folder id of state.filteredItems

      return response
    } finally {
      state.isFetchingFolderItems = false
    }
  }
  state.favoritesFetcher = async function (payload?: GetFavoritesParams) {
    try {
      state.isFetchingFavorites = true
      const response = await onFavoritesFetch(payload)
      state.favorites = convertFavoritesToDict(response.results)

      return response
    } finally {
      state.isFetchingFavorites = false
    }
  }
  state.favoriteCreator = async function (payload: FavoriteCreateRequest) {
    try {
      state.isCreatingFavorite = true
      const response = await onFavoriteCreate(payload)
      const key = response.page?.id ?? response.file?.id ?? response.folder?.id
      if (key)
        state.favorites = {
          ...state.favorites,
          [key]: response,
        }

      return response
    } catch (e) {
      // todo when SDK API will contain error type wrap with the condition for codes: 404 | 400
      await state.favoritesFetcher?.()
      throw e
    } finally {
      state.isCreatingFavorite = false
    }
  }
  state.favoriteDeleter = async function (payload: Favorite['id']) {
    const { removeSelectedItems, selectedSection } = useAppStore()
    try {
      state.isDeletingFavorite = true
      const favorite = Object.values(state.favorites).find((favorite) => favorite.id === payload)
      const pageFileFolderId = favorite?.page?.id ?? favorite?.file?.id ?? favorite?.folder?.id //pageId has priority over fileId

      if (!pageFileFolderId) return

      await onFavoriteDelete(payload)

      delete state.favorites[pageFileFolderId]

      if (selectedSection.value === 'favorites') {
        removeSelectedItems([pageFileFolderId])
      }
    } catch (e) {
      // todo when SDK API will contain error type wrap with the condition for codes: 404 | 400
      await state.favoritesFetcher?.()
      throw e
    } finally {
      state.isDeletingFavorite = false
    }
  }
  state.favoritesDeleter = async function (payload: BulkDeleteRequest): Promise<BulkDeleteResponse> {
    const { removeSelectedItems, selectedSection } = useAppStore()
    try {
      state.isDeletingFavorite = true

      const response = await onFavoritesDelete(payload)

      const deletedIdSet = new Set(response.deleted_ids)

      state.favorites = Object.entries(state.favorites).reduce<Record<string, Favorite>>((acc, [key, favorite]) => {
        const pageFileFolderId = favorite.page?.id ?? favorite.file?.id ?? favorite.folder?.id
        if (!deletedIdSet.has(favorite.id) && pageFileFolderId && !deletedIdSet.has(pageFileFolderId)) {
          acc[key] = favorite
        }
        return acc
      }, {})

      if (selectedSection.value === 'favorites') {
        removeSelectedItems(response.deleted_ids)
      }

      return response
    } finally {
      state.isDeletingFavorite = false
    }
  }
  state.recentFilesFetcher = async function () {
    try {
      state.isFetchingRecentFiles = true
      const response = await onRecentFilesFetch()
      state.recent = response

      return response
    } finally {
      state.isFetchingRecentFiles = false
    }
  }
}

const onUploadFilesRefetchFolder = () => {
  state.isFetchingFolderItems = true
  return debouncedFolderItemsFetcher()
}

const debouncedFolderItemsFetcher = debounce(
  async () => {
    // do not refetch for files uploaded within the last second
    try {
      await state.folderItemsFetcher?.(state.currentFolderId)
      state.isFetchingFolderItems = false
    } catch (error) {
      state.isFetchingFolderItems = false
      console.error('exposed onUploadComplete: ', error)
    }
  },
  1000,
  { leading: false, trailing: true },
)

export type {
  FolderItemsFetcher,
  FolderCreator,
  FolderUpdater,
  FolderDeleter,
  FoldersDeleter,
  FileCreator,
  FileUpdater,
  FileOpener,
  FileDeleter,
  FilesDeleter,
  FileFetcher,
  FileListFetcher,
  ItemsMoverItem,
  ItemsMover,
  FavoritesFetcher,
  FavoriteCreator,
  FavoriteDeleter,
  FavoritesDeleter,
  FavoritesSetter,
  CompactFile,
  CompactFolder,
  RecentFilesFetcher,
  SelectDeviceFile,
  FolderLister,
}

export default () => ({
  state: readonly(state),
  getters: readonly(getters),
  actions: {
    init,
    getInitialState,
    setFavorites,
    setCurrentFolder,
    setSearchQuery,
    setIsFetchingListFiles,
    setSearchMetadataFilters,
    setFilteredItems,
    onUpsertFolder,
    onUploadFilesRefetchFolder,
    clearTimeouts,
    reset: () => Object.assign(state, getInitialState()),
  },
})
